import React, { useEffect, useState } from 'react';
import './InstallPrompt.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(ios);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS prompt after 10 seconds if not installed
    if (ios && !localStorage.getItem('iosPromptShown')) {
      setTimeout(() => {
        setShowPrompt(true);
        localStorage.setItem('iosPromptShown', 'true');
      }, 10000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for 7 days
    localStorage.setItem('promptDismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="install-prompt">
      <div className="install-prompt-content">
        <img src="/favicon.png" alt="Green UET" className="install-prompt-icon" />
        <div className="install-prompt-text">
          <h3>Install Green UET</h3>
          <p>Install our app for a better experience!</p>
        </div>
        {isIOS ? (
          <div className="ios-install-instructions">
            <p>Tap <span className="share-icon">⎙</span> then "Add to Home Screen"</p>
            <button onClick={handleDismiss} className="install-dismiss-btn">Got it</button>
          </div>
        ) : (
          <div className="install-prompt-actions">
            <button onClick={handleDismiss} className="install-dismiss-btn">Later</button>
            <button onClick={handleInstall} className="install-btn">Install</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;